import type { BuildersAddon, BuildersAddonAssignment, BuildersAddonOption, Member } from "../api/contract";
import { AppError } from "../api/problem";
import { formatAmount, seatTotalLabel } from "./offers";

// The Builders add-on, read once for every surface that shows it: the Overview notice,
// the Billing card, the Organization users badge and actions, and the Discord card gate.
// Everything here is pure so the rules are unit-testable without mounting a page.
//
// Nothing in this module hard-codes a price or a plan name: every figure comes from the
// add-on endpoint's `option`, and every bound from its `minQuantity` / `maxQuantity`.

const format = new Intl.NumberFormat("en-US");

/** The Billing page section the seat-scope notice links to (`/billing#builders-addon`). */
export const BUILDERS_ADDON_SECTION_ID = "builders-addon";

/** Org ids whose Overview notice was dismissed in this browser (a JSON string array). */
export const BUILDERS_ADDON_NOTICE_KEY = "epidev.notice.buildersAddon.dismissed";

/** Plenty for anyone; it only stops a long-lived browser from growing the list forever. */
const MAX_DISMISSED_ORGS = 50;

function validOption(option: BuildersAddonOption | null | undefined): option is BuildersAddonOption {
  return !!option && Number.isFinite(option.unitAmountCents) && option.unitAmountCents >= 0 && typeof option.currency === "string";
}

/**
 * The add-on's unit price with its unit spelled out: "$30 per month" on Pro (it covers the
 * account's one seat, so it needs no unit) and "$20 per seat / month" on Team and
 * Enterprise. Null when the endpoint published no usable option.
 */
export function addonPriceLabel(addon: Pick<BuildersAddon, "scope" | "option"> | null | undefined): string | null {
  if (!addon?.scope || !validOption(addon.option)) return null;
  const period: string = addon.option.interval;
  const price = formatAmount(addon.option.unitAmountCents, addon.option.currency);
  return addon.scope === "seat" ? `${price} per seat / ${period}` : `${price} per ${period}`;
}

/** What a seat count costs: "$60 per month for 3 seats" — unit amount × quantity, never a fixed total. */
export function addonTotalLabel(option: BuildersAddonOption | null | undefined, quantity: number | null | undefined): string | null {
  return validOption(option) ? seatTotalLabel(option, quantity) : null;
}

/**
 * Whether the organization has bought the add-on. Pro (`individual`) counts it bought
 * while it is active; Team / Enterprise (`seat`) count it bought once any subscription
 * exists, even with every seat still unassigned — buying more is a quantity change then.
 */
export function isAddonPurchased(addon: Pick<BuildersAddon, "scope" | "active" | "subscription">): boolean {
  if (addon.scope === "individual") return addon.active;
  if (addon.scope === "seat") return addon.subscription !== null;
  return false;
}

/**
 * Whether the add-on is on sale to this organization: its base takes the add-on and sales
 * are open. Builders is then sold as the add-on, so the standalone Builders plan is not
 * promoted as an upgrade on top of the same base. With sales off the plan stays the way in.
 */
export function isAddonOnSale(addon: Pick<BuildersAddon, "eligibility" | "salesEnabled" | "scope"> | null | undefined): boolean {
  return !!addon && addon.eligibility === "eligible" && addon.salesEnabled && !!addon.scope;
}

/**
 * The Overview notice: only for someone who can buy it (`canPurchase` already folds in
 * `billing.manage`, the sales flag, and eligibility), only while it is not yet bought,
 * only with a price to show, and never after this organization's notice was dismissed.
 */
export function shouldShowBuildersAddonNotice(addon: BuildersAddon | null | undefined, dismissed: boolean): boolean {
  if (!addon || dismissed || !addon.canPurchase) return false;
  if (!addon.scope || !validOption(addon.option)) return false;
  return !isAddonPurchased(addon);
}

/**
 * The Billing card: hidden where the add-on does not apply (Builders already includes it,
 * Community and legacy levels, no active plan), and hidden when sales are off and there is
 * nothing bought to show. An existing purchase stays visible with sales off.
 */
export function shouldShowBuildersAddonCard(addon: BuildersAddon | null | undefined): boolean {
  if (!addon || addon.eligibility !== "eligible" || !addon.scope || !validOption(addon.option)) return false;
  const bought = isAddonPurchased(addon) || addon.subscription !== null;
  return addon.salesEnabled || bought;
}

// ------------------------------------------------------------- seat quantity

export interface AddonQuantityBounds {
  /** The catalog floor (`minQuantity`). */
  min: number;
  /** The base plan's seat limit (`maxQuantity`). */
  max: number;
  /** Add-on seats assigned right now; the quantity may not drop below this. */
  assigned: number;
  /** The quantity currently paid for, or null before the first purchase. */
  current: number | null;
}

export function addonQuantityBounds(addon: Pick<BuildersAddon, "minQuantity" | "maxQuantity" | "assignedSeats" | "subscription">): AddonQuantityBounds {
  const min = Math.max(1, addon.minQuantity);
  return {
    min,
    max: Math.max(min, addon.maxQuantity),
    assigned: Math.max(0, addon.assignedSeats ?? 0),
    current: addon.subscription?.quantity ?? null
  };
}

/** The lowest quantity the stepper may offer: the catalog floor or the assigned seats. */
export function addonQuantityFloor(bounds: AddonQuantityBounds): number {
  return Math.min(bounds.max, Math.max(bounds.min, bounds.assigned));
}

/** Keeps a stepped quantity inside the floor and the plan's ceiling. */
export function clampAddonQuantity(value: number, bounds: AddonQuantityBounds): number {
  if (!Number.isFinite(value)) return addonQuantityFloor(bounds);
  return Math.min(bounds.max, Math.max(addonQuantityFloor(bounds), Math.round(value)));
}

const seatsWord = (n: number): string => `${format.format(n)} add-on ${n === 1 ? "seat" : "seats"}`;

/**
 * Why a typed add-on seat count cannot be submitted, or null when it can. Mirrors the
 * server's refusals (`invalid_quantity`, `addon_quantity_below_assigned`,
 * `addon_no_change`) so the owner hears the rule before a round trip.
 */
export function addonQuantityIssue(raw: string, bounds: AddonQuantityBounds): string | null {
  const trimmed = raw.trim();
  if (trimmed === "") return "Enter how many add-on seats you want.";
  if (!/^\d+$/.test(trimmed)) return "Add-on seats must be a whole number.";
  const quantity = Number(trimmed);
  if (quantity < bounds.min) return `Choose at least ${seatsWord(bounds.min)}.`;
  if (quantity > bounds.max) {
    return `You can have up to ${seatsWord(bounds.max)} — one for each seat on your plan.`;
  }
  if (quantity < bounds.assigned) {
    return `${seatsWord(bounds.assigned)} ${bounds.assigned === 1 ? "is" : "are"} assigned right now. Remove the add-on from members in Organization users before going below that.`;
  }
  if (bounds.current !== null && quantity === bounds.current) {
    return "That's your current add-on seat count. Choose a different number to review a change.";
  }
  return null;
}

// ------------------------------------------------------------- refusals

/** Friendly reading of the add-on problem codes (PLAN §4.7 / §5); null falls back to the server's detail. */
export function buildersAddonErrorMessage(code: string | null | undefined): string | null {
  switch (code) {
    case "invalid_quantity":
      return "That number of add-on seats isn't available. Choose a quantity within the limits shown.";
    case "request_invalid":
      return "That request wasn't valid. Refresh the page and try again.";
    case "addon_not_applicable":
      return "The Builders add-on isn't available on your current plan.";
    case "addon_already_active":
      return "Builders is already active on your plan. Refresh the page to see it.";
    case "addon_quantity_below_assigned":
      return "More add-on seats are assigned than that. Remove the add-on from members in Organization users first, then try again.";
    case "addon_no_change":
      return "That's already your add-on seat count.";
    case "addon_multiple_subscriptions":
      return "Your organization has more than one Builders add-on subscription. Contact support@epidev.ai so we can combine them before you make changes.";
    case "addon_activation_pending":
      return "Your Builders add-on purchase is being confirmed with Stripe. Refresh in a moment.";
    case "feature_disabled":
      return "The Builders add-on can't be bought or changed right now. Please try again later or contact support@epidev.ai.";
    case "billing_unavailable":
      return "Billing is temporarily unavailable. Please try again in a few minutes.";
    case "addon_assignment_not_applicable":
      return "On this plan the add-on applies to your seat automatically, so there's nothing to assign.";
    case "addon_not_active":
      return "Buy add-on seats on the Billing page before assigning them.";
    case "extension_seat_required":
      return "Assign this member an extension seat first — the Builders add-on rides on their seat.";
    case "addon_capacity_exceeded":
      return "Every add-on seat is in use. Buy more on the Billing page, or remove the add-on from someone else first.";
    case "addon_assignment_not_found":
      return "This member doesn't hold the Builders add-on.";
    case "membership_inactive":
      return "Reactivate this member before giving them the Builders add-on.";
    default:
      return null;
  }
}

/**
 * Whether a failed session request should keep its idempotency key, so a retry replays the
 * same server-side attempt instead of starting a second one. Kept when the request may not
 * have been decided — a dropped connection, a 5xx / 503, or the first attempt still running
 * (`idempotency_in_progress`). A 4xx refusal is a decided answer: the next try is a new attempt.
 */
export function keepsIdempotencyKey(error: unknown): boolean {
  if (!(error instanceof AppError)) return true;
  if (error.code === "idempotency_in_progress") return true;
  return error.status === 0 || error.status >= 500;
}

// ------------------------------------------------------------- members

/** The member's active add-on assignment, if any. */
export function buildersAssignmentFor(
  addon: Pick<BuildersAddon, "assignments"> | null | undefined,
  userId: string
): BuildersAddonAssignment | null {
  return addon?.assignments.find((assignment) => assignment.userId === userId) ?? null;
}

export interface BuildersSeatAction {
  kind: "assign" | "revoke";
  label: "Assign Builders add-on" | "Remove Builders add-on";
  /** Why the action is unavailable, shown beside the disabled control; null when allowed. */
  disabledReason: string | null;
}

type SeatMember = Pick<Member, "user" | "membership" | "seatAssignment">;

/**
 * What an assigner may do for one member, or null when assignment is not theirs to make
 * (`canAssign` folds in `seats.manage`, the seat scope, and an active add-on). A holder
 * can always be released; a newcomer needs an active membership, an extension seat, and
 * a free add-on seat.
 */
export function buildersSeatAction(addon: BuildersAddon | null | undefined, member: SeatMember): BuildersSeatAction | null {
  if (!addon?.canAssign) return null;
  if (buildersAssignmentFor(addon, member.user.id)) {
    return { kind: "revoke", label: "Remove Builders add-on", disabledReason: null };
  }
  let disabledReason: string | null = null;
  if (member.membership.status !== "active") disabledReason = "Reactivate this member first";
  else if (member.seatAssignment?.status !== "assigned") disabledReason = "Needs an extension seat first";
  else if ((addon.availableSeats ?? 0) <= 0) disabledReason = "No add-on seats free";
  return { kind: "assign", label: "Assign Builders add-on", disabledReason };
}

// ------------------------------------------------------------- Discord

export interface DiscordGateInput {
  addon: Pick<BuildersAddon, "viewer" | "scope"> | null | undefined;
  /** Licence access level (the pre-add-on rule). */
  accessLevel: string | null | undefined;
  canManageBilling: boolean;
}

/**
 * How the Overview offers Builders Discord, or null when it does not.
 *
 * - `invitation`: the emailed invite card. Invitations are one per customer, addressed to
 *   the billing owner, and their routes require `billing.manage` and accept only the
 *   Builders plan or an individual (Pro) add-on — so this is the Builders plan's owner (the
 *   pre-add-on rule, which holds even with the add-on switched off) and a Pro owner whose
 *   add-on is active.
 * - `link`: Discord account linking (OAuth). The route for everyone a Team / Enterprise
 *   add-on seat covers — the billing owner included, since the invitation routes refuse
 *   seat scope.
 *
 * Plan-sourced benefits are reported for every member of a Builders-plan organization,
 * but Discord on that plan stays owner-only (PLAN §5.1, "unchanged").
 */
export function buildersDiscordMode(input: DiscordGateInput): "invitation" | "link" | null {
  if (input.accessLevel === "builder" && input.canManageBilling) return "invitation";
  const viewer = input.addon?.viewer;
  if (!viewer?.hasBuildersBenefits) return null;
  if (viewer.source === "addon" && input.addon?.scope === "seat") return "link";
  if (input.canManageBilling) return "invitation";
  return viewer.source === "plan" ? null : "link";
}

/** Discord's OAuth callback outcome (`?discord=linked|failed` on Overview), or null. */
export function parseDiscordLinkReturn(value: string | null | undefined): "linked" | "failed" | null {
  const normalized = value?.trim().toLowerCase();
  return normalized === "linked" || normalized === "failed" ? normalized : null;
}

// ------------------------------------------------------------- notice dismissal

/** Tolerant read of the stored org-id list: anything malformed reads as "none dismissed". */
export function parseDismissedOrgIds(raw: string | null | undefined): string[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return [...new Set(parsed.filter((id): id is string => typeof id === "string" && id.length > 0 && id.length <= 128))];
  } catch {
    return [];
  }
}

/** Adds an org id, keeping the newest dismissals when the list is at its cap. */
export function withDismissedOrg(ids: readonly string[], orgId: string): string[] {
  const next = [...ids.filter((id) => id !== orgId), orgId];
  return next.slice(-MAX_DISMISSED_ORGS);
}

// ------------------------------------------------------------- Stripe return

export type AddonReturn = "success" | "updated" | "canceled";

/** The `?addon=` value Stripe sends the buyer back with, or null for anything else. */
export function parseAddonReturn(value: string | null | undefined): AddonReturn | null {
  const normalized = value?.trim().toLowerCase();
  if (normalized === "success" || normalized === "updated") return normalized;
  if (normalized === "canceled" || normalized === "cancelled" || normalized === "cancel") return "canceled";
  return null;
}

/**
 * What this tab asked Stripe for. Kept in sessionStorage only for the round trip, so the
 * return can tell "still waiting for the webhook" from "done" without assuming success.
 */
export interface AddonChangeMemory {
  orgId: string;
  quantity: number;
  /** Quantity paid for before the change; null for a first purchase. */
  previousQuantity: number | null;
}

const ADDON_CHANGE_KEY = "epidev.billing.buildersAddonChange";

export function rememberAddonChange(memory: AddonChangeMemory): void {
  try {
    window.sessionStorage.setItem(ADDON_CHANGE_KEY, JSON.stringify(memory));
  } catch {
    // Storage denied: the purchase still proceeds; the return just polls on what it sees.
  }
}

/** Read once per return: taking it clears it so a reload does not re-announce. */
export function takeAddonChange(orgId: string): AddonChangeMemory | null {
  try {
    const raw = window.sessionStorage.getItem(ADDON_CHANGE_KEY);
    if (!raw) return null;
    window.sessionStorage.removeItem(ADDON_CHANGE_KEY);
    const parsed = JSON.parse(raw) as Partial<AddonChangeMemory>;
    if (parsed.orgId !== orgId || typeof parsed.quantity !== "number") return null;
    return {
      orgId,
      quantity: parsed.quantity,
      previousQuantity: typeof parsed.previousQuantity === "number" ? parsed.previousQuantity : null
    };
  } catch {
    return null;
  }
}

/**
 * Whether the add-on state now reflects what the buyer confirmed in Stripe. A first
 * purchase is settled once it reads as bought; a quantity change once the purchased
 * quantity has moved off what it was. An `updated` return with no memory of the
 * starting quantity cannot be judged and is never reported as settled.
 */
export function addonReturnSettled(kind: AddonReturn, addon: BuildersAddon | null | undefined, memory: AddonChangeMemory | null): boolean {
  if (kind === "canceled") return true;
  if (!addon) return false;
  if (kind === "success") return isAddonPurchased(addon);
  if (!addon.subscription || !memory) return false;
  return addon.subscription.quantity !== memory.previousQuantity;
}
